import { useEffect, useState } from "react";
import { BarChart3, Eye, Heart, Star, Ticket, DollarSign, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const MONTHS_SHORT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

export const StatsView = () => {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/pro/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  if (!stats) {
    return <div className="mt-6 h-64 animate-pulse rounded-3xl bg-white" />;
  }

  const max = Math.max(1, ...stats.daily_views.map((d) => d.count));
  const totals = [
    { icon: Eye, label: "Vues totales", value: stats.totals.views, color: "#7C3AED", bg: "#F5F3FF" },
    { icon: Heart, label: "Favoris", value: stats.totals.favorites, color: "#EF4444", bg: "#FEF2F2" },
    { icon: Star, label: "Note moyenne", value: String(stats.totals.reviews_avg).replace(".", ","), color: "#F59E0B", bg: "#FFFBEB" },
    { icon: Ticket, label: "Réservations", value: stats.totals.reservations, color: "#14B8A6", bg: "#F0FDFA" },
    { icon: CalendarDays, label: "Événements", value: stats.totals.events, color: "#2563EB", bg: "#EFF6FF" },
    { icon: DollarSign, label: "Revenus", value: `${stats.totals.revenue.toFixed(2).replace(".", ",")} $`, color: "#16A34A", bg: "#F0FDF4" },
  ];

  return (
    <div className="mt-6 space-y-5" data-testid="pro-stats-view">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {totals.map((t) => (
          <div key={t.label} className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm" data-testid={`stat-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl" style={{ backgroundColor: t.bg }}>
              <t.icon size={18} style={{ color: t.color }} />
            </div>
            <p className="mt-3 text-xl font-extrabold text-slate-900">{t.value}</p>
            <p className="text-xs text-slate-400">{t.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <BarChart3 className="h-4 w-4 text-[#2563EB]" />
          Vues des 14 derniers jours
        </h2>
        <div className="mt-6 flex h-40 items-end gap-1.5 sm:gap-2.5" data-testid="views-chart">
          {stats.daily_views.map((d) => {
            const day = new Date(`${d.date}T12:00:00`);
            return (
              <div key={d.date} className="group flex flex-1 flex-col items-center gap-1.5" title={`${d.count} vue(s)`}>
                <span className="text-[10px] font-semibold text-slate-400 opacity-0 transition group-hover:opacity-100">{d.count}</span>
                <div
                  data-testid={`bar-${d.date}`}
                  className="w-full rounded-t-lg bg-[#2563EB]/80 transition group-hover:bg-[#2563EB]"
                  style={{ height: `${Math.max(4, (d.count / max) * 110)}px` }}
                />
                <span className="text-[9px] text-slate-400">
                  {day.getDate()} {MONTHS_SHORT[day.getMonth()]}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-slate-400">
          {t("Les vues comptent chaque visite de ta fiche établissement et de tes événements.")}
        </p>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <Ticket className="h-4 w-4 text-[#14B8A6]" />
          Réservations des 14 derniers jours
        </h2>
        <div className="mt-6 flex h-40 items-end gap-1.5 sm:gap-2.5" data-testid="reservations-chart">
          {(stats.daily_reservations || []).map((d) => {
            const day = new Date(`${d.date}T12:00:00`);
            const maxRes = Math.max(1, ...(stats.daily_reservations || []).map((x) => x.count));
            return (
              <div key={d.date} className="group flex flex-1 flex-col items-center gap-1.5" title={`${d.count} réservation(s)`}>
                <span className="text-[10px] font-semibold text-slate-400 opacity-0 transition group-hover:opacity-100">{d.count}</span>
                <div
                  data-testid={`res-bar-${d.date}`}
                  className="w-full rounded-t-lg bg-[#14B8A6]/80 transition group-hover:bg-[#14B8A6]"
                  style={{ height: `${Math.max(4, (d.count / maxRes) * 110)}px` }}
                />
                <span className="text-[9px] text-slate-400">
                  {day.getDate()} {MONTHS_SHORT[day.getMonth()]}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-slate-400">
          {t("Les réservations comptent chaque place réservée sur tes événements.")}
        </p>
      </div>
    </div>
  );
};
